/**
 * The v1 shapes, served from the v2 API.
 *
 * `backend/projects/` proxied every one of these calls to Convex. Deleting it means the
 * pages have to be fed from `/api/v2/orgs/<slug>/...` instead — and the two APIs disagree
 * about almost every field name, because v1's shapes are Convex documents and v2's are the
 * Postgres model.
 *
 * **This file is a translation layer with a known end date.** It exists so the data layer
 * could move in one step instead of nine, and so a page-by-page migration to the native v2
 * shapes can happen afterwards without a running app ever depending on Convex. Every
 * function here is a seam: when a page is ported, its entry stops being called and can go.
 * Nothing new should be built against these shapes.
 *
 * The awkward parts, all of which are v1 shapes being narrower than the model underneath:
 *
 *   - **`_id` carries the work item's KEY, not its numeric id.** v2 addresses work items as
 *     `ACME-14` because a number alone is per project and not unique in an organization —
 *     that ambiguity was a confirmed live defect. The key is what every v2 route needs, so
 *     it is what the pages must hold.
 *   - **`status` is five strings; v2 has workflow states as rows.** Mapped by slug, both
 *     ways. The five slugs are seeded by `provision_organization` and pinned by
 *     `DefaultWorkflowVocabularyTests`, so this mapping cannot silently drift — but a
 *     workspace that adds a sixth state has no v1 name for it, and `toStatus` says so
 *     rather than guessing.
 *   - **v1 timestamps are epoch milliseconds; v2 uses ISO 8601.** Converted at the edge.
 *   - **v2 lists are `{count, results}`; v1 lists are bare arrays.** Unwrapped, because
 *     roughly twenty call sites do `Array.isArray(res.data)` and would render empty
 *     against an envelope — silently, with no error anywhere.
 */
import type { AxiosResponse } from 'axios';

import type {
  Activity, Assignee, DashboardStats, Issue, Member, Notification, Project, Reviewer,
  RunningTimer, Subtask, Task, TaskPriority, TaskStatus, TimeLog,
} from './types';
import type { ReviewState } from './legacyTypes';

// --- the current workspace ---------------------------------------------------------

/**
 * The slug every v2 route is scoped by.
 *
 * Read from the stored session rather than passed down, because the v1 signatures these
 * functions have to keep do not carry it — the workspace was implicit in the token. A
 * missing slug throws rather than defaulting: a request to the wrong workspace answers 404
 * and would read as "no data" on every page at once.
 */
export function currentOrgSlug(): string {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem('auth_user');
  } catch {
    stored = null;
  }
  const slug = stored ? (JSON.parse(stored) as { portal_name?: string })?.portal_name : '';
  if (!slug) {
    throw new Error(
      'No workspace is selected. Every v2 route is scoped to one, and guessing would '
      + 'answer 404 on every page at once.',
    );
  }
  return slug;
}

export const orgUrl = (path: string) => `/api/v2/orgs/${currentOrgSlug()}/${path}`;

// --- lists -------------------------------------------------------------------------

type Page<T> = { count: number; results: T[] };

/** v2 paginates; v1 did not. Roughly twenty call sites check `Array.isArray(res.data)`. */
export function unwrap<T>(response: AxiosResponse<Page<T> | T[]>): AxiosResponse<T[]> {
  const data = response.data;
  const rows = Array.isArray(data) ? data : (data?.results ?? []);
  return { ...response, data: rows } as AxiosResponse<T[]>;
}

// --- times -------------------------------------------------------------------------

/** ISO 8601 to epoch milliseconds. */
export const toMillis = (iso?: string | null): number | null =>
  iso ? new Date(iso).getTime() : null;

/** Epoch milliseconds (or a date input's `YYYY-MM-DD`) to an ISO instant. */
export const toIso = (value?: number | string | null): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = typeof value === 'number' ? new Date(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** v2 `spent_on` and `period_start` are calendar dates, not instants. */
export const toDate = (value?: number | string | null): string | null => {
  const iso = toIso(value);
  return iso ? iso.slice(0, 10) : null;
};

// --- statuses ----------------------------------------------------------------------

/**
 * The five state slugs `provision_organization` seeds, and the v1 names for them.
 *
 * Both directions, from one table, so they cannot drift apart the way three hardcoded
 * lists in TasksTab did — where the board omitted `Cancelled` and cancelling a task made
 * it vanish with no column to land in.
 */
const STATE_TO_STATUS: Record<string, TaskStatus> = {
  'todo': 'Open',
  'in-progress': 'In Progress',
  'in-review': 'In Review',
  'on-hold': 'On Hold',
  'done': 'Completed',
  'cancelled': 'Cancelled',
};

const STATUS_TO_STATE: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(STATE_TO_STATUS).map(([slug, status]) => [status, slug]),
  ),
  // Issues carry a second status vocabulary over the same five states (see
  // STATUS_TO_ISSUE_STATUS, which is this map's read-direction inverse). Without these,
  // `stateSlugFor('Fixed')` returned undefined and moving an issue to Fixed or Closed
  // through the shared updateWorkItem path did nothing — no transition, no error, the
  // optimistic UI snapping back on the next refetch. Open and In Progress already resolved
  // via the task names above; these are the issue-only ones.
  'Fixed': 'done',
  'Closed': 'cancelled',
  'Reopened': 'todo',
};

/**
 * A workflow state's v1 status name.
 *
 * A workspace can add states — they are rows precisely so it can — and such a state has no
 * v1 name. Falling back to `Open` would silently file it under a status it is not, so this
 * returns the state's own name and lets the caller show something true.
 */
export const toStatus = (state?: { slug?: string; name?: string } | null): TaskStatus =>
  (state?.slug ? STATE_TO_STATUS[state.slug] : undefined)
  ?? ((state?.name as TaskStatus) || 'Open');

export const stateSlugFor = (status?: string | null): string | undefined =>
  status ? STATUS_TO_STATE[status] : undefined;

// --- work items --------------------------------------------------------------------

type V2Ref = { id?: number; key?: string; name?: string; slug?: string } | null;
/**
 * A member as v2 nests it. There is NO `email` — the field is `invited_email`.
 *
 * The previous version of this type declared `email?: string`, which the v2 API has never
 * returned: `MemberRefSerializer` emits id, display_name, invited_email, avatar, role and
 * status. Because the property was optional, `assignee?.email` type-checked and silently
 * evaluated to undefined on every row, so every assigned work item reached the UI as
 * unassigned. A hand-written type that claims a field the server does not send turns a
 * wrong field name into a silent null instead of a compile error — the same way a
 * hand-written InviteInfo once claimed `hasAccount` and pinned a whole branch to false.
 */
type V2Member = {
  id?: number;
  invited_email?: string;
  display_name?: string;
  avatar?: string;
  role?: string;
  status?: string;
} | null;

/** The member's address, under whichever key this payload uses. */
const memberEmail = (m: V2Member | undefined): string =>
  (m && (m.invited_email ?? (m as { email?: string } | null)?.email)) || '';

/** The member's name, falling back to the address so a cell is never blank. */
const memberName = (m: V2Member | undefined): string =>
  (m && m.display_name) || memberEmail(m);

/**
 * Two letters for an avatar, from a name where there is one.
 *
 * Every surface built these itself as `assignee.slice(0, 2).toUpperCase()` on what the
 * adapter handed it — an email — so `akshata@rooman.com` showed as "AK" and
 * `dev1@demo.test` as "DE". Initials taken from an address are initials of the address.
 * "Akshata T Rathod" is AR; a single name gives its first two letters; and an address with
 * no name behind it still yields something rather than nothing.
 */
export const initialsFrom = (name?: string | null, email?: string | null): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (email || '').slice(0, 2).toUpperCase();
};

/** The same rule, for a member object straight off the wire. */
export const memberInitials = (m: V2Member | undefined): string =>
  initialsFrom(m && m.display_name, memberEmail(m));

/** Project the assignee set into UI rows, dropping any without an address. */
const memberList = (members?: V2Member[]): Assignee[] =>
  (members || [])
    .map((m) => ({ email: memberEmail(m), name: memberName(m), initials: memberInitials(m) }))
    .filter((a) => a.email);

/** A reviewer link row off the wire: a member plus their decision. */
interface V2Reviewer {
  member: V2Member;
  state: ReviewState;
  decided_at: string | null;
}

/** Project the reviewer set into UI rows, keeping each reviewer's decision. */
const reviewerList = (reviewers?: V2Reviewer[]): Reviewer[] =>
  (reviewers || [])
    .map((r) => ({
      email: memberEmail(r.member),
      name: memberName(r.member),
      initials: memberInitials(r.member),
      state: r.state,
      decidedAt: toMillis(r.decided_at),
    }))
    .filter((r) => r.email);

export interface V2WorkItem {
  id: number;
  key: string;
  number: number;
  project: V2Ref;
  type: V2Ref;
  state: V2Ref & { category?: string };
  parent: V2Ref;
  //: Carries its goal, so a task can name the whole Goal → Module → task chain without a
  //: second fetch. Kept in step with `ModuleRef` in the generated schema.
  module?: (V2Ref & { status?: string; goal?: V2Ref | null }) | null;
  title: string;
  description: string;
  assignee: V2Member;
  assignees?: V2Member[];
  reviewers?: V2Reviewer[];
  reporter: V2Member;
  priority: (V2Ref & { rank?: number }) | null;
  severity?: (V2Ref & { rank?: number }) | null;
  estimate_minutes: number | null;
  /** Minutes logged against this item; null when the endpoint did not annotate it. */
  logged_minutes?: number | null;
  /** First entry into a doing-category state; null if work never started. */
  first_started_at?: string | null;
  /** Last entry into a done-category state; null whenever the item is not closed. */
  completed_at?: string | null;
  due_at: string | null;
  start_at: string | null;
  completion_percent: number;
  created_at: string;
  updated_at: string;
}

/**
 * A v2 work item as the v1 `Task` the pages consume.
 *
 * `_id` is the KEY. Every v2 route addresses a work item as `ACME-14`, because a number
 * alone is per project and not unique in an organization — resolving on it returned
 * whichever row the index reached first, which is the defect this schema was rebuilt to
 * remove. The pages pass `_id` straight back into `tasks.update(projectId, taskId, …)`, so
 * it has to be the value those routes take.
 */
export function asTask(item: V2WorkItem): Task {
  return {
    _id: item.key,
    portal: '',
    projectId: item.project?.key ?? '',
    projectName: item.project?.name ?? null,
    // The task this is a subtask of, so its page can lead back up. `parentTaskId` was
    // declared on Task and never filled in.
    parentTaskId: item.parent?.key ?? null,
    parentName: (item.parent as { title?: string } | null)?.title ?? null,
    taskNumber: item.key,
    name: item.title,
    description: item.description || '',
    assignee: memberEmail(item.assignee) || null,
    assigneeName: memberName(item.assignee) || null,
    assigneeInitials: item.assignee ? memberInitials(item.assignee) : null,
    assignees: memberList(item.assignees),
    reviewers: reviewerList(item.reviewers),
    // Declared on Task from the start and never filled in — only the ISSUE shape mapped it —
    // so "who proposed this?" on the approvals queue read blank for every task.
    reporter: memberEmail(item.reporter) || null,
    reporterName: memberName(item.reporter) || null,
    status: toStatus(item.state),
    priority: (item.priority?.name as TaskPriority) || 'None',
    approvalStatus: (item as Record<string, any>).approval_status ?? null,
    approvedByName: memberName((item as Record<string, any>).approved_by) || null,
    rejectionReason: (item as Record<string, any>).rejection_reason || null,
    // The TYPE this row is. v2 has one table and `Bug` is a type on it, so the shared
    // detail page needs this to know whether it is showing a task or a bug.
    typeSlug: item.type?.slug ?? null,
    typeName: item.type?.name ?? null,
    moduleId: item.module?.id ?? null,
    moduleName: item.module?.name ?? null,
    goalId: item.module?.goal?.id ?? null,
    goalName: item.module?.goal?.name ?? null,
    startDate: toMillis(item.start_at),
    dueDate: toMillis(item.due_at),
    completionPercent: item.completion_percent ?? 0,
    estimatedHours: item.estimate_minutes === null || item.estimate_minutes === undefined
      ? null
      : item.estimate_minutes / 60,
    // Hours actually logged against this item. The field was declared on Task and rendered
    // in two places -- the task page's "Actual Hours" and the timer card's "Logged so far"
    // -- but nothing ever set it, so both read empty however much time was logged. Rounded
    // to one decimal because the raw value is minutes and 1.1666..h helps nobody.
    actualHours: item.logged_minutes == null
      ? null
      : Math.round(item.logged_minutes / 6) / 10,
    // Real flow timestamps, recorded by the state transition itself. `completedAt` is
    // null whenever the item is not currently closed, and null for anything closed before
    // the audit trail existed -- callers must treat null as "not recorded", never as zero.
    startedAt: toMillis(item.first_started_at),
    completedAt: toMillis(item.completed_at),
    createdAt: toMillis(item.created_at) ?? 0,
    updatedAt: toMillis(item.updated_at) ?? 0,
  } as Task;
}

/** The same row seen as a v1 `Issue`. v2 has one WorkItem table; `Bug` is a type on it. */
const PRIORITY_TO_SEVERITY: Record<string, string> = {
  None: 'Minor', Low: 'Minor', Medium: 'Major', High: 'Critical', Critical: 'Blocker',
};

// On Hold is its own status, not a kind of In Progress. Folding it in meant an issue somebody
// had parked read 'In Progress' in its row and was counted under the In Progress chip, and
// because its select already showed In Progress, choosing In Progress fired no change, so the
// list could not move a parked issue back to work at all. The write direction already
// resolves 'On Hold' to the on-hold state through STATUS_TO_STATE.
const STATUS_TO_ISSUE_STATUS: Record<string, string> = {
  'Open': 'Open', 'In Progress': 'In Progress', 'In Review': 'In Review',
  'On Hold': 'On Hold',
  'Completed': 'Fixed', 'Cancelled': 'Closed',
};

export function asIssue(item: V2WorkItem): Issue {
  const task = asTask(item);
  const priorityName = item.priority?.name || 'Medium';
  return {
    _id: item.key,
    portal: '',
    projectId: item.project?.key ?? '',
    issueNumber: item.key,
    title: item.title,
    description: item.description || '',
    assignee: memberEmail(item.assignee) || null,
    assigneeName: memberName(item.assignee) || null,
    assigneeInitials: item.assignee ? memberInitials(item.assignee) : null,
    assignees: memberList(item.assignees),
    reporter: memberEmail(item.reporter),
    reporterName: memberName(item.reporter),
    // The stored severity when the issue has one — it is a real, separate field now — and
    // only falling back to a priority-derived guess for older rows that never carried one.
    severity: (item.severity?.name as string) || PRIORITY_TO_SEVERITY[priorityName] || 'Major',
    status: STATUS_TO_ISSUE_STATUS[task.status] ?? 'Open',
    priority: (priorityName === 'None' ? 'Low' : priorityName),
    dueDate: task.dueDate,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
  } as unknown as Issue;
}

/** And as a v1 `Subtask`: a work item whose `parent` is set. */
export function asSubtask(item: V2WorkItem): Subtask {
  return {
    _id: item.key,
    portal: '',
    projectId: item.project?.key ?? '',
    parentTaskId: item.parent?.key ?? '',
    name: item.title,
    // Declared on Subtask and never mapped, so a subtask's description could not be seen
    // from its parent — the list had the text and threw it away.
    description: item.description || '',
    status: toStatus(item.state) === 'Completed' ? 'Completed' : 'Open',
    assignee: memberEmail(item.assignee) || null,
    assigneeName: memberName(item.assignee) || null,
    assigneeInitials: item.assignee ? memberInitials(item.assignee) : null,
    assignees: memberList(item.assignees),
    dueDate: toMillis(item.due_at),
    createdAt: toMillis(item.created_at) ?? 0,
  } as unknown as Subtask;
}

/**
 * A v1 task payload as a v2 work-item write.
 *
 * Only keys the caller actually sent are included: v2 write serializers are strict and a
 * PATCH carrying `undefined` for every unspecified field would either clear it or 400.
 */
/**
 * Assign a work item, or unassign it with an empty string.
 *
 * v2 has had a dedicated action for this since the API was written and nothing in the
 * frontend had ever called it — `grep "assign/" frontend/src` returned nothing. It is
 * preferable to a field PATCH for the same change: the service records the activity with
 * verb `assigned` and the affected member attached, and it writes the notification that
 * tells the person they have been given work. A PATCH records `updated`.
 */
export async function assignWorkItem(key: string, email: string): Promise<void> {
  const trimmed = (email || '').trim();
  const assignee = trimmed ? await resolveId('member', trimmed) : null;
  await request.post(orgUrl(`work-items/${encodeURIComponent(key)}/assign/`), { assignee });
}

/** Set the whole assignee set at once (multi-select). Emails resolve to member ids; the
 *  server keeps the first as the primary. An empty list clears every assignee. */
export async function assignWorkItemMany(key: string, emails: string[]): Promise<void> {
  const ids = await Promise.all(
    (emails || []).map((e) => resolveId('member', String(e).trim())),
  );
  await request.post(orgUrl(`work-items/${encodeURIComponent(key)}/assign/`), { assignees: ids });
}

/** Submit the current user's own review decision on a work item (approve / request changes
 *  / back to pending). Returns the refreshed task so the caller can repaint the panel. */
export async function reviewWorkItem(key: string, state: ReviewState): Promise<Task> {
  const res = await request.post(
    orgUrl(`work-items/${encodeURIComponent(key)}/review/`), { state },
  );
  return asTask(res.data as V2WorkItem);
}

/** Correct when finished work was really finished (Owner/Admin/Manager). The server writes
 *  the correction into the task's history beside the original close. */
export async function correctCompletion(key: string, completedAt: string): Promise<Task> {
  const res = await request.post(
    orgUrl(`work-items/${encodeURIComponent(key)}/completion/`), { completed_at: completedAt },
  );
  return asTask(res.data as V2WorkItem);
}

//: Words the UI uses to mean "no value", which are not the names of any row. Sending these
//: to be resolved is how a menu option this app drew became an error about a missing record.
const NO_VALUE_NAMES = new Set(['none', 'no priority', 'no severity', 'unset', '—', '-']);

export async function taskWrite(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  const set = (key: string, value: unknown) => {
    if (value !== undefined) out[key] = value;
  };

  set('title', payload.name);
  set('description', payload.description);
  set('completion_percent', payload.completionPercent);
  if (payload.dueDate !== undefined) out.due_at = toIso(payload.dueDate as number);
  if (payload.startDate !== undefined) out.start_at = toIso(payload.startDate as number);
  // `null` is an explicit clear, like `assignee` and `priority` below — skipping it is why
  // an estimate could be set but never removed. `undefined` still means "not specified".
  if (payload.estimatedHours !== undefined) {
    out.estimate_minutes = payload.estimatedHours === null
      ? null
      : Math.round(Number(payload.estimatedHours) * 60);
  }
  // The module a task lives in (Goal → Module → this). Already a v2 id in the UI, so no
  // resolution — `null` explicitly moves the task back to the backlog, `undefined` leaves it
  // untouched under strict PATCH semantics.
  if (payload.moduleId !== undefined) {
    out.module = payload.moduleId === null ? null : Number(payload.moduleId);
  }

  // `assignee` and `priority` used to be absent from this function entirely, and that is
  // the whole reason assignment did not work: every create and edit modal collects them,
  // every one of the six write methods for tasks, issues and subtasks funnels through
  // here, and both keys were dropped before the body was built. The request then answered
  // 201 or 200, so nothing reported a failure — the item was simply created unassigned.
  //
  // Not the email-versus-primary-key mismatch it looked like from the read side. The field
  // never reached the wire at all.
  //
  // The UI holds an assignee as an email and a priority as a name; v2 takes primary keys,
  // hence the resolution. `''` is an explicit clear, which is why it maps to null rather
  // than being skipped — `undefined` means "not specified in this PATCH" and must stay
  // absent, because v2 write serializers are strict.
  if (payload.assignee !== undefined) {
    const email = String(payload.assignee ?? '').trim();
    out.assignee = email ? await resolveId('member', email) : null;
  }
  // The full set: a list of emails from the multi-select. Resolved to member ids the same
  // way. When present it is authoritative (the server makes the first its primary), so it
  // takes precedence over `assignee` above.
  if (payload.assignees !== undefined) {
    const emails = (payload.assignees as string[] | null) || [];
    out.assignees = await Promise.all(emails.map((e) => resolveId('member', String(e).trim())));
  }
  // Requested reviewers: a list of emails from the reviewer multi-select, resolved to member
  // ids the same way. An empty list clears every request; the server keeps a kept reviewer's
  // existing decision rather than resetting it to pending.
  if (payload.reviewers !== undefined) {
    const emails = (payload.reviewers as string[] | null) || [];
    out.reviewers = await Promise.all(emails.map((e) => resolveId('member', String(e).trim())));
  }
  if (payload.priority !== undefined) {
    const name = String(payload.priority ?? '').trim();
    // 'None' is the UI's word for no priority, not the name of a level — no workspace has a
    // row called None. Resolving it threw "No priority None in this workspace", which the
    // modal could only report as "The task could not be saved", so choosing None looked like
    // a broken save rather than a value this app invented and never mapped.
    out.priority = name && !NO_VALUE_NAMES.has(name.toLowerCase())
      ? await resolveId('priority', name)
      : null;
  }
  // Issues carry a severity (Minor/Major/Critical/Blocker) distinct from priority. It was
  // collected by the new-issue form and dropped here, so it never persisted and was shown
  // back derived from priority instead. Now it is resolved to the SEVERITY row's id like
  // priority. Tasks never send it, so this branch is inert for them.
  if (payload.severity !== undefined) {
    const name = String(payload.severity ?? '').trim();
    out.severity = name && !NO_VALUE_NAMES.has(name.toLowerCase())
      ? await resolveId('severity', name)
      : null;
  }
  return out;
}

// --- everything else ---------------------------------------------------------------

export function asProject(row: Record<string, any>): Project {
  return {
    _id: row.key,
    portal: '',
    projectId: row.key,
    name: row.name,
    description: row.description || '',
    owner: memberEmail(row.owner),
    ownerName: memberName(row.owner),
    clientName: row.client?.name ?? '',
    projectGroup: row.group || '',
    priority: row.priority || 'Medium',
    tags: Array.isArray(row.tags) ? row.tags : [],
    status: row.status,
    startDate: toMillis(row.start_on),
    endDate: toMillis(row.end_on),
    completionPercent: row.completion_percent ?? 0,
    color: row.color || '',
    // Annotated by the viewset. Without it every project card read "0 tasks".
    taskCount: row.work_item_count ?? 0,
    createdAt: toMillis(row.created_at) ?? 0,
    updatedAt: toMillis(row.updated_at) ?? 0,
  } as unknown as Project;
}

export function asMember(row: Record<string, any>): Member {
  return {
    _id: String(row.id),
    portal: '',
    email: row.email ?? row.invited_email ?? '',
    // `row.email` again, which v2 does not send — the `??` above rescues the address but
    // this line had no fallback, so an unnamed member's name derived from `undefined` and
    // came out empty. It feeds the assignee picker's option labels.
    name: row.display_name || (row.email ?? row.invited_email ?? '').split('@')[0],
    role: row.role,
    status: row.status,
    avatar: '',
    joinedAt: toMillis(row.created_at) ?? undefined,
  } as unknown as Member;
}

export function asTimeLog(row: Record<string, any>): TimeLog {
  const minutes = row.minutes ?? 0;
  return {
    _id: String(row.id),
    portal: '',
    projectId: row.project?.key ?? '',
    taskId: row.work_item_key ?? null,
    taskName: row.work_item?.title ?? null,
    userEmail: memberEmail(row.membership),
    userName: memberName(row.membership) || null,
    logDate: toMillis(row.spent_on) ?? 0,
    // v1 stored hours and minutes separately, which permitted `1h 90m`. v2 stores one
    // integer; splitting it here cannot produce an inconsistent pair.
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
    notes: row.notes || '',
    isBillable: row.billing_status === 'billable',
    approvalStatus: row.approval_status,
    createdAt: toMillis(row.created_at) ?? 0,
  } as unknown as TimeLog;
}

export function asRunningTimer(row: Record<string, any>): RunningTimer {
  return {
    id: row.id,
    taskKey: row.work_item_key ?? '',
    startedAt: toMillis(row.started_at) ?? 0,
  };
}

export function asNotification(row: Record<string, any>): Notification {
  return {
    _id: String(row.id),
    portal: '',
    toEmail: '',
    type: row.kind,
    message: row.message,
    link: row.link || null,
    isRead: !!row.is_read,
    createdAt: toMillis(row.created_at) ?? 0,
  } as unknown as Notification;
}

/**
 * An activity event as the v1 `Activity` the feed renders.
 *
 * `entityType` is always an audit entity now. v1 also used `"chat"`, `"dm"` and `"forum"`
 * here, because the Collaboration page wrote messages into this table — direct messages
 * included, readable by every member of the workspace. v2 has no write path at all, so
 * those values cannot occur.
 */
export function asActivity(row: Record<string, any>): Activity {
  const change = (row.changes ?? [])[0];
  return {
    _id: String(row.id),
    portal: '',
    projectId: row.project?.key ?? null,
    actorEmail: row.actor_email || '',
    actorName: row.actor || row.actor_email || '',
    action: row.verb,
    entityType: row.work_item_key ? 'workItem' : 'project',
    entityId: row.work_item_key ?? row.project?.key ?? '',
    entityName: row.target_label || '',
    metadata: change
      ? JSON.stringify({ field: change.field, from: change.old_display, to: change.new_display })
      : '',
    //: How many fields the event changed — the line shows the first and says how many more,
    //: rather than implying a six-field edit touched one.
    changeCount: (row.changes ?? []).length,
    createdAt: toMillis(row.at) ?? 0,
  } as unknown as Activity;
}

/**
 * One entry in a task's own history.
 *
 * Distinct from `asActivity`, which flattens an event to its FIRST change because the global
 * feed renders one line per row. A task's history is the one place the whole change set
 * matters: an edit that moved the due date and the estimate together is one event, and
 * showing only the due date makes the estimate look like it changed by itself.
 */
export interface TaskHistoryEntry {
  id: string;
  verb: string;
  //: What the event was about. For the task itself it is "KEY — title"; for something on the
  //: task — an attachment, a time entry — it names that, so the line is not "created this task".
  label: string;
  actorName: string;
  at: number;
  changes: Array<{ field: string; from: string; to: string }>;
}

export function asTaskHistory(row: Record<string, any>): TaskHistoryEntry {
  return {
    id: String(row.id),
    verb: String(row.verb || ''),
    label: String(row.target_label || ''),
    // `actor` is null for anything the system did on its own — a timer banked by a
    // transition, a backfill. "System" is honest; the signed-in user's name would not be.
    actorName: row.actor || row.actor_email || 'System',
    at: toMillis(row.at) ?? 0,
    changes: (row.changes ?? []).map((c: Record<string, any>) => ({
      field: String(c.field ?? ''),
      from: String(c.old_display ?? ''),
      to: String(c.new_display ?? ''),
    })),
  };
}

export function asDashboardStats(row: Record<string, any>): DashboardStats {
  return {
    totalProjects: row.total_projects ?? 0,
    activeProjects: row.active_projects ?? 0,
    completedProjects: row.completed_projects ?? 0,
    totalTasks: row.total_work_items ?? 0,
    openTasks: row.todo_work_items ?? 0,
    inProgressTasks: row.doing_work_items ?? 0,
    // Closed minus cancelled: `Category` has three values and cancelled work lands in
    // DONE, so adding them together would report abandoned work as delivered.
    completedTasks: Math.max(
      0, (row.done_work_items ?? 0) - (row.cancelled_work_items ?? 0),
    ),
    // Kept rather than dropped. Reports was deriving a residual from the other four counts
    // and labelling it "On Hold"; the arithmetic makes that residual exactly this number,
    // so the chart has been showing cancelled work under the wrong name.
    cancelledTasks: row.cancelled_work_items ?? 0,
    // Open work assigned to the CURRENT user, counted server-side off the assignee SET
    // (a task with two owners counts for both). It was computed on every dashboard
    // request and then discarded here, so the one figure on the page that is actually
    // about you was the one the UI never showed.
    myOpenTasks: row.my_open_work_items ?? 0,
    openIssues: row.open_bugs ?? 0,
  } as unknown as DashboardStats;
}

// --- request helpers ---------------------------------------------------------------
//
// These need the axios instance, which lives in ./index. Injected rather than imported to
// keep the cycle out: index imports this module for the translation, so this module
// cannot import index back.

type Requester = {
  get: (url: string) => Promise<AxiosResponse<any>>;
  post: (url: string, body?: unknown) => Promise<AxiosResponse<any>>;
  patch: (url: string, body?: unknown) => Promise<AxiosResponse<any>>;
  delete: (url: string) => Promise<AxiosResponse<any>>;
};

let request: Requester;

/** Called once from ./index, which owns the axios instance and its interceptors. */
export function useRequester(instance: Requester) {
  request = instance;
}

/**
 * Work items, filtered.
 *
 * `project` is a key such as `ACME`; v2 matches it case-insensitively. `assignee=me`
 * resolves server-side from the caller's membership, so the client never has to know its
 * own membership id.
 */
export async function listWorkItems(
  filters: Record<string, string> = {},
  shape: (item: V2WorkItem) => unknown = asTask,
): Promise<AxiosResponse<any[]>> {
  const params = new URLSearchParams(filters);
  // v2 paginates at 50 (max 200). Reading a single page silently truncated every board and
  // list to the first 50 items — tasks 51+ simply did not appear, and the count read as 50
  // however many there really were. Ask for the largest page and follow `next` to the end.
  if (!params.has('page_size')) params.set('page_size', '200');
  let next = orgUrl(`work-items/?${params.toString()}`);
  const rows: V2WorkItem[] = [];
  let guard = 0;
  let lastResponse: AxiosResponse<any> | undefined;
  while (next && guard < 50) {
    guard += 1;
    const response = await request.get(next);
    lastResponse = response;
    rows.push(...unwrap<V2WorkItem>(response).data);
    const body = response.data as { next?: string | null };
    // The `next` link is absolute; strip the origin so it goes through the same axios
    // instance and its auth interceptor.
    next = body && body.next ? body.next.replace(/^https?:\/\/[^/]+/, '') : '';
  }
  return { ...(lastResponse as AxiosResponse<any>), data: rows.map(shape) } as AxiosResponse<any[]>;
}

/**
 * Create a work item.
 *
 * `type` is a slug — `task` or `bug` — because work item types are rows and a workspace
 * can rename them; the seeded slugs are what stays stable. `parentKey` is what makes a
 * subtask a subtask: v2 has one table, and a subtask is an item with a parent.
 */
/**
 * Primary keys for a project key and a work-item type slug.
 *
 * v2 addresses projects by key in URLs (`/projects/NWSITE/`) but its write serializers take
 * primary keys, because `TenantRelatedField` is a `PrimaryKeyRelatedField` — so posting
 * `{"project": "NWSITE", "type": "task"}` answers
 * `Incorrect type. Expected pk value, received str.` That is what creating any task,
 * issue or subtask did after the port, and the stubbed tests missed it because a stub
 * returning `{}` for every write never validates the payload.
 *
 * Cached for the session. These are catalogue rows: a project's key and a type's slug do
 * not change — `delivery_project.key` is immutable at the database — so a lookup per
 * create would be a request to learn something already known.
 */
/**
 * What can be looked up by its natural key, and where.
 *
 * `member` and `priority` are here because the write translator needs them: the UI holds an
 * assignee as an email and a priority as a name ('High'), while v2 write serializers take
 * primary keys. Without a resolver there was nowhere to put those two fields, so
 * `taskWrite` simply omitted them — see the comment there.
 */
const RESOLVABLE: Record<string, {
  path: string;
  field: string;
  /** Narrows which rows from `path` may be cached under this kind. */
  where?: (row: Record<string, any>) => boolean;
}> = {
  project: { path: 'projects/', field: 'key' },
  type: { path: 'work-item-types/', field: 'slug' },
  member: { path: 'members/', field: 'invited_email' },
  // Priority and severity are two KINDS of PriorityLevel in one table, served by one
  // endpoint — so each must only ever match its own kind.
  //
  // Matching on name alone was justified by "their names never collide (Urgent/High/Medium/
  // Low vs Minor/Major/Critical/Blocker)". That is true of the server's vocabulary and false
  // of the one the UI offers: the task form's priority list is None/Low/Medium/High/Critical,
  // so choosing Critical resolved to the SEVERITY row named Critical and posted its id as a
  // priority. The create serializer predicates `priority` on the PRIORITY kind, so the
  // request came back `priority: Invalid pk "7" - object does not exist.` — a 400 blaming a
  // primary key the user never saw, for a value they picked from a menu this app drew.
  priority: {
    path: 'priority-levels/', field: 'name',
    where: (row) => String(row.kind || '').toLowerCase() === 'priority',
  },
  severity: {
    path: 'priority-levels/', field: 'name',
    where: (row) => String(row.kind || '').toLowerCase() === 'severity',
  },
} as const;

type ResolvableKind = keyof typeof RESOLVABLE;

const idCache = new Map<string, number>();

/** A project key's primary key, for the writes that take one (recurring tasks). */
export const projectPk = (key: string): Promise<number> => resolveId('project', key);

async function resolveId(kind: ResolvableKind, natural: string): Promise<number> {
  // Keyed by workspace. Project keys and type slugs are unique per organization, not
  // globally — the same `ACME` exists in two workspaces as two different rows — so a
  // cache without the slug in its key posts the previous workspace's primary key after a
  // switch, and the composite foreign keys reject it as a cross-tenant reference. The
  // switcher never calls clearIdCache(), so the key has to carry the tenant itself.
  const slug = currentOrgSlug();
  const cacheKey = `${slug}:${kind}:${natural.toLowerCase()}`;
  const hit = idCache.get(cacheKey);
  if (hit !== undefined) return hit;

  const { path, field, where } = RESOLVABLE[kind];

  // Every page, not just the first. v2 paginates at 50, so reading one page meant
  // creating a task or logging time failed for any project past the 50th — with an
  // error that says the project does not exist, which is the least helpful way to be
  // wrong about it.
  let next = orgUrl(`${path}?page_size=200`);
  let guard = 0;
  while (next && guard < 50) {
    guard += 1;
    const response = await request.get(next);
    for (const row of unwrap<Record<string, any>>(response).data) {
      if (!row[field]) continue;
      if (where && !where(row)) continue;
      idCache.set(`${slug}:${kind}:${String(row[field]).toLowerCase()}`, row.id);
    }
    const body = response.data as { next?: string | null };
    // The `next` link is absolute; strip the origin so it goes through the same axios
    // instance and its auth interceptor.
    next = body && body.next ? body.next.replace(/^https?:\/\/[^/]+/, '') : '';
  }

  const resolved = idCache.get(cacheKey);
  if (resolved === undefined) {
    throw new Error(
      `No ${kind} ${natural} in this workspace. It has to exist before something can be `
      + 'created against it.',
    );
  }
  return resolved;
}

/**
 * One work item's primary key, from its `ACME-14` key.
 *
 * Not cached: there can be thousands, they are created constantly, and the lookup is a
 * single exact GET rather than a scan. `parent` and `work_item` are both
 * `TenantRelatedField`, so both need this — which is why logging time against a task and
 * creating a subtask were failing the same way creating a task was.
 */
/**
 * Walk every page of a v2 list endpoint.
 *
 * v2 paginates at 50 and caps `page_size` at 200; reading only the first page is a SILENT
 * truncation — the caller gets an array and no indication that more exists. That is how
 * `time-entries/` was being read on Timesheets, Approvals and Reports, so a workspace with
 * more than fifty entries saw totals that were simply wrong, with nothing on screen to say
 * so.
 *
 * The guard bounds a pathological loop (a server that keeps returning `next`) rather than
 * the data: at 200 a page it allows 10,000 rows, and the same guard is used for work items.
 */
export async function fetchAllPages<T>(path: string, shape: (row: any) => T): Promise<AxiosResponse<T[]>> {
  let next = orgUrl(path);
  const rows: any[] = [];
  let guard = 0;
  let lastResponse: AxiosResponse<any> | undefined;
  while (next && guard < 50) {
    guard += 1;
    const response = await request.get(next);
    lastResponse = response;
    rows.push(...unwrap<any>(response).data);
    const body = response.data as { next?: string | null };
    // The `next` link is absolute; strip the origin so it goes back through the same axios
    // instance and its auth interceptor.
    next = body && body.next ? body.next.replace(/^https?:\/\/[^/]+/, '') : '';
  }
  return { ...(lastResponse as AxiosResponse<any>), data: rows.map(shape) } as AxiosResponse<T[]>;
}

/**
 * One work item, by key.
 *
 * The task page used to read the WHOLE project — `listWorkItems` follows every page at 200
 * a time — and then `.find()` the one it wanted. On a project with 600 tasks that is four
 * round trips and 600 fully-serialised rows (each with its labels, custom fields, assignees
 * and reviewers) to render a single page, repeated after every status change, every edit and
 * every timer stop. It also meant a task beyond the 50-page guard could not be opened at all.
 */
export async function fetchWorkItem(key: string): Promise<AxiosResponse<Task>> {
  const response = await request.get(orgUrl(`work-items/${encodeURIComponent(key)}/`));
  return { ...response, data: asTask(response.data) } as AxiosResponse<Task>;
}

/**
 * The multipart body for attaching a file to a work item.
 *
 * The write serializer takes PRIMARY KEYS — `TenantRelatedField` is a
 * `PrimaryKeyRelatedField` — while the UI holds a project key and a task key, so both are
 * resolved here. Posting the keys answers `Incorrect type. Expected pk value, received str.`,
 * the same trap every other write in this adapter exists to avoid.
 *
 * `project` is sent alongside `work_item` deliberately: an attachment is a project document
 * that names a task, so it stays in the project's library, and the server checks the two
 * agree rather than trusting either alone.
 *
 * Returns the body rather than sending it — the shared requester takes no per-call config,
 * and multipart needs its own content type, so the POST belongs with the axios instance in
 * `api/index.ts`. The translation is what lives here.
 */
/** One comment in a task's conversation. */
export interface TaskComment {
  id: number;
  body: string;
  authorName: string;
  authorEmail: string;
  authorInitials: string;
  at: number;
  editedAt: number | null;
  parentId: number | null;
}

export function asComment(row: Record<string, any>): TaskComment {
  return {
    id: Number(row.id),
    body: String(row.body ?? ''),
    authorName: memberName(row.author) || memberEmail(row.author) || 'Somebody',
    authorEmail: memberEmail(row.author) || '',
    authorInitials: row.author ? memberInitials(row.author) : '?',
    at: toMillis(row.created_at) ?? 0,
    editedAt: toMillis(row.edited_at),
    parentId: row.parent ?? null,
  };
}

/** A task's conversation, oldest first. */
export async function listComments(taskKey: string): Promise<TaskComment[]> {
  const res = await request.get(orgUrl(`comments/?work_item=${encodeURIComponent(taskKey)}&page_size=200`));
  return unwrap<Record<string, any>>(res).data.map(asComment);
}

/** Post to a task's conversation. `mentions` are the addresses the author @-mentioned. */
export async function postComment(taskKey: string, body: string, mentions: string[] = []): Promise<TaskComment> {
  const workItem = await resolveWorkItemId(taskKey);
  const res = await request.post(orgUrl('comments/'), { work_item: workItem, body, mentions });
  return asComment(res.data);
}

export async function editComment(id: number, body: string): Promise<TaskComment> {
  const res = await request.patch(orgUrl(`comments/${id}/`), { body });
  return asComment(res.data);
}

export async function deleteComment(id: number): Promise<void> {
  await request.delete(orgUrl(`comments/${id}/`));
}

export async function attachmentFormData(
  projectKey: string,
  taskKey: string,
  file: File,
): Promise<FormData> {
  const [projectId, itemId] = await Promise.all([
    resolveId('project', projectKey),
    resolveWorkItemId(taskKey),
  ]);
  const form = new FormData();
  form.append('project', String(projectId));
  form.append('work_item', String(itemId));
  // The stored name is the file's own. It is shown back verbatim and never used as a path —
  // `document_upload_path` builds the path from ids, not from this.
  form.append('name', file.name);
  form.append('file', file);
  return form;
}

export async function resolveWorkItemId(key: string): Promise<number> {
  const response = await request.get(orgUrl(`work-items/${encodeURIComponent(key)}/`));
  const id = (response.data as { id?: number })?.id;
  if (id === undefined) throw new Error(`No work item ${key} in this workspace.`);
  return id;
}

/** Exported for tests, and so a workspace switch does not carry the old ids over. */
export function clearIdCache() {
  idCache.clear();
}

/**
 * Create a work item.
 *
 * `typeSlug` is a slug — `task` or `bug` — because work item types are rows and a
 * workspace can rename them; the seeded slugs are what stays stable. `parentKey` is what
 * makes a subtask a subtask: v2 has one table, and a subtask is an item with a parent.
 */
export async function createWorkItem(
  projectKey: string,
  payload: Record<string, unknown>,
  typeSlug: 'task' | 'bug',
  parentKey?: string,
): Promise<AxiosResponse<any>> {
  const [projectId, typeId] = await Promise.all([
    resolveId('project', projectKey),
    resolveId('type', typeSlug),
  ]);

  const body: Record<string, unknown> = {
    ...(await taskWrite(payload)),
    project: projectId,
    type: typeId,
  };
  if (parentKey) body.parent = await resolveWorkItemId(parentKey);
  if (!body.title) body.title = (payload.name as string) || (payload.title as string) || '';

  const response = await request.post(orgUrl('work-items/'), body);
  const created = response.data as { key?: string; state?: { slug?: string } };

  // The create endpoint has no state field: a new item is born in its workflow's initial
  // state. So the status the form chose — the Kanban column a card was added to, the
  // modal's Status select, a subtask created as Completed — was silently dropped, and the
  // item appeared in the wrong column, snapping back on the next refetch. Apply it as a
  // follow-up transition when it differs from where the item landed.
  const wantSlug = stateSlugFor(payload.status as string);
  if (created.key && wantSlug && wantSlug !== created.state?.slug) {
    return updateWorkItem(created.key, { status: payload.status });
  }
  return { ...response, data: asTask(response.data) };
}

/**
 * Create many work items in one call — the bulk-add paste.
 *
 * The project, type and any shared assignee/priority resolve to primary keys once, not
 * once per title, and the server creates the whole batch in a single transaction. Returns
 * the created items as v1 `Task`s in the order given.
 */
export async function bulkCreateWorkItems(
  projectKey: string,
  titles: string[],
  shared: { assignee?: string; priority?: string; type?: 'task' | 'bug' } = {},
): Promise<AxiosResponse<Task[]>> {
  const [project, type] = await Promise.all([
    resolveId('project', projectKey),
    resolveId('type', shared.type || 'task'),
  ]);
  const body: Record<string, unknown> = { project, type, titles };
  if (shared.assignee) body.assignee = await resolveId('member', shared.assignee);
  if (shared.priority) body.priority = await resolveId('priority', shared.priority);

  const response = await request.post(orgUrl('work-items/bulk/'), body);
  const rows = Array.isArray(response.data) ? (response.data as V2WorkItem[]) : [];
  return { ...response, data: rows.map(asTask) };
}

/** A person on a project, with the role scoped to that project. */
export interface ProjectMember {
  id: number;
  project: string;
  member: {
    id: number; display_name: string; invited_email: string;
    avatar: string; role: string; status: string;
  };
  role: string;
  created_at: string;
}

export async function listProjectMembers(projectKey: string): Promise<AxiosResponse<ProjectMember[]>> {
  const response = await request.get(orgUrl(`project-members/?project=${encodeURIComponent(projectKey)}`));
  return { ...response, data: unwrap<ProjectMember>(response).data };
}

/** Add a workspace member (by email) to a project. The server resolves the email and the
 *  project key to ids and enforces that only ACTIVE members can be seated. */
export async function addProjectMember(
  projectKey: string, email: string, role: string,
): Promise<AxiosResponse<ProjectMember>> {
  const [project, membership] = await Promise.all([
    resolveId('project', projectKey),
    resolveId('member', email),
  ]);
  return request.post(orgUrl('project-members/'), { project, membership, role });
}

export async function updateProjectMemberRole(id: number, role: string): Promise<AxiosResponse<ProjectMember>> {
  return request.patch(orgUrl(`project-members/${id}/`), { role });
}

export async function removeProjectMember(id: number): Promise<AxiosResponse<void>> {
  return request.delete(orgUrl(`project-members/${id}/`));
}

export type ProjectVisibility = 'workspace' | 'team';

export async function getProjectVisibility(projectKey: string): Promise<ProjectVisibility> {
  const response = await request.get(orgUrl(`projects/${encodeURIComponent(projectKey)}/`));
  return (response.data?.visibility as ProjectVisibility) || 'workspace';
}

export async function setProjectVisibility(
  projectKey: string, visibility: ProjectVisibility,
): Promise<AxiosResponse<unknown>> {
  return request.patch(orgUrl(`projects/${encodeURIComponent(projectKey)}/`), { visibility });
}

// --- Goals and modules: Project → Goal → Module → Task → Subtask ---------------------

export type GoalStatus = 'Active' | 'On Hold' | 'Completed' | 'Archived';

/** A tracked objective in a project. `completion_percent` and the counts are rolled up by
 *  the server from the work items under the goal's modules — read-only. */
export interface Goal {
  id: number;
  project: string;
  name: string;
  description: string;
  status: GoalStatus;
  owner: V2Member | null;
  start_on: string | null;
  end_on: string | null;
  order: number;
  completion_percent: number;
  module_count: number;
  work_item_count: number;
  /** The phase (time-based stage) this goal is delivered in, by id; null when none. */
  phase?: number | null;
}

/** A component of a goal. Tasks live in a module through `WorkItem.module`. */
export interface Module {
  id: number;
  goal: { id: number; name: string; status: GoalStatus };
  project: string;
  name: string;
  description: string;
  status: GoalStatus;
  owner: V2Member | null;
  order: number;
  completion_percent: number;
  work_item_count: number;
}

/** Fields a create/edit form sends. `owner` is a member id (or null); the rest are plain. */
export interface GoalWrite {
  name?: string;
  description?: string;
  status?: GoalStatus;
  owner?: number | null;
  start_on?: string | null;
  end_on?: string | null;
  order?: number;
  phase?: number | null;
}

export async function listGoals(projectKey: string): Promise<Goal[]> {
  const response = await request.get(
    orgUrl(`goals/?project=${encodeURIComponent(projectKey)}`),
  );
  return unwrap<Goal>(response).data;
}

export async function createGoal(projectKey: string, body: GoalWrite): Promise<Goal> {
  const project = await resolveId('project', projectKey);
  const response = await request.post(orgUrl('goals/'), { project, ...body });
  return response.data as Goal;
}

export async function updateGoal(id: number, body: GoalWrite): Promise<Goal> {
  const response = await request.patch(orgUrl(`goals/${id}/`), body);
  return response.data as Goal;
}

export async function deleteGoal(id: number): Promise<void> {
  await request.delete(orgUrl(`goals/${id}/`));
}

/** Modules under one goal, or all modules in a project when `goalId` is omitted. */
export async function listModules(
  opts: { goalId?: number; projectKey?: string },
): Promise<Module[]> {
  const params = new URLSearchParams();
  if (opts.goalId) params.set('goal', String(opts.goalId));
  if (opts.projectKey) params.set('project', opts.projectKey);
  const response = await request.get(orgUrl(`modules/?${params.toString()}`));
  return unwrap<Module>(response).data;
}

export async function createModule(
  goalId: number, body: Omit<GoalWrite, 'start_on' | 'end_on'>,
): Promise<Module> {
  const response = await request.post(orgUrl('modules/'), { goal: goalId, ...body });
  return response.data as Module;
}

export async function updateModule(
  id: number, body: Omit<GoalWrite, 'start_on' | 'end_on'>,
): Promise<Module> {
  const response = await request.patch(orgUrl(`modules/${id}/`), body);
  return response.data as Module;
}

export async function deleteModule(id: number): Promise<void> {
  await request.delete(orgUrl(`modules/${id}/`));
}

/** One parsed CSV row. Every field but `title` is an optional natural-key string the server
 *  resolves — an assignee's email, a priority's name — so nothing is resolved here. */
export interface ImportRow {
  title: string;
  description?: string;
  assignee_email?: string;
  priority?: string;
  severity?: string;
  status?: string;
  type?: string;
  //: The module to file the row under, by name, within the project being imported into.
  module?: string;
  due_date?: string;
  start_date?: string;
  estimate_hours?: string;
}

export interface ImportWarning {
  row: number;
  field: string;
  value: string;
  message: string;
}

export interface ImportResult {
  created: number;
  items: Task[];
  warnings: ImportWarning[];
}

/**
 * CSV import: one item per row, each with its own assignee (matched by email), priority,
 * severity, type, status and due date. Unlike bulkCreate, resolution is the server's job —
 * the rows carry emails and names verbatim, and the response reports any that did not match
 * rather than failing the whole upload.
 */
export async function importWorkItems(
  projectKey: string,
  rows: ImportRow[],
): Promise<AxiosResponse<ImportResult>> {
  const project = await resolveId('project', projectKey);
  const response = await request.post(orgUrl('work-items/import/'), { project, rows });
  const data = (response.data || {}) as { created?: number; items?: V2WorkItem[]; warnings?: ImportWarning[] };
  return {
    ...response,
    data: {
      created: data.created ?? 0,
      items: (data.items || []).map(asTask),
      warnings: data.warnings || [],
    },
  };
}

/**
 * Update a work item, addressed by key.
 *
 * A status change is a transition, not a field write: v2 models state as a workflow
 * position, and moving between them is `POST /transition/` so the move can be validated
 * and recorded. Sending `state` as an ordinary field would bypass both.
 */
export async function updateWorkItem(
  key: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse<any>> {
  const stateSlug = stateSlugFor(payload.status as string);
  const fields = await taskWrite(payload);

  let response: AxiosResponse<any> | null = null;
  // Transition FIRST, field PATCH second. The two are separate requests — there is no
  // combined endpoint — so the order decides what a partial failure leaves behind. A
  // status change to Completed carries `completion_percent: 100` alongside it; when the
  // transition failed but the PATCH had already run, the item was left at 100% AND still
  // Open, a contradiction the user could see and not explain. Doing the move first means a
  // rejected transition throws before anything is written, and the optimistic UI rolls the
  // whole change back cleanly.
  if (stateSlug) {
    // `/transition/` takes the target state's PRIMARY KEY — `state` there is a
    // PrimaryKeyRelatedField — but the adapter holds a slug (`in-progress`), and posting
    // the slug answered 400 and surfaced as "Task status could not be updated" on every
    // status change in the product. The item's own transitions endpoint lists its current
    // and reachable states with both id and slug, scoped to this item's workflow, so the
    // slug resolves unambiguously even when several workflows each have a `todo`.
    const view = await request.get(orgUrl(`work-items/${key}/transitions/`));
    const body = view.data as { current?: V2Ref; available?: V2Ref[] };
    const current = body.current ?? null;
    const target = [current, ...(body.available ?? [])]
      .find((s) => s && s.slug === stateSlug) ?? null;
    if (target && target.id !== undefined && target.id !== current?.id) {
      response = await request.post(orgUrl(`work-items/${key}/transition/`), {
        state: target.id,
      });
    } else if (!target) {
      // The slug is not a state of this workflow — a v1 status with no v2 state, or a
      // renamed one. Say so rather than silently no-op, which would leave the optimistic
      // UI showing a move the server never made.
      throw new Error(`No state "${stateSlug}" on this item's workflow.`);
    }
  }
  if (Object.keys(fields).length) {
    response = await request.patch(orgUrl(`work-items/${key}/`), fields);
  }
  if (!response) response = await request.get(orgUrl(`work-items/${key}/`));
  return { ...response, data: asTask(response.data) };
}

/**
 * Resolve an email to a membership id, then act on it.
 *
 * v1 addressed members by email in the URL; v2 uses the membership's primary key, because
 * an address is not stable and is not unique across workspaces. One extra read, on a page
 * that has already listed the members.
 */
export async function withMemberId<T>(
  email: string,
  act: (id: number) => Promise<T>,
): Promise<T> {
  // Through the shared resolver rather than its own fetch. This used to GET `members/` with
  // no `page_size`, so it saw only the first page: in a workspace past 50 members, assigning
  // to anyone below the cut answered "No member with the address …" — a sentence that blames
  // the address for a paging bug. It also refetched the whole roster on every single assign,
  // while `taskWrite` right above resolved the same emails from a cache.
  const wanted = (email || '').trim();
  try {
    return await act(await resolveId('member', wanted));
  } catch (err) {
    // `resolveId` phrases its miss for creates ("it has to exist before something can be
    // created against it"), which is the wrong sentence for an assignment.
    if (err instanceof Error && err.message.startsWith('No member ')) {
      throw new Error(`No member with the address ${email} in this workspace.`);
    }
    throw err;
  }
}

/** A v1 project payload as a v2 project write. */
export async function projectWrite(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  const set = (key: string, value: unknown) => {
    if (value !== undefined) out[key] = value;
  };
  set('name', payload.name);
  set('description', payload.description);
  set('status', payload.status);
  set('color', payload.color);
  set('group', payload.projectGroup);
  // The client is free text in the v1 form; the backend turns the name into a Client row
  // (found or created). Sent as `client_name`, not `client`, because the form has no id.
  if (payload.clientName !== undefined) out.client_name = payload.clientName;
  set('priority', payload.priority);
  // A JSON list of labels. Only sent when the form actually provides it, so a partial
  // update that omits tags leaves the stored ones alone rather than clearing them.
  if (Array.isArray(payload.tags)) out.tags = payload.tags;
  // The key is the project's permanent identifier — it appears in every work-item key
  // (ACME-14) and is never re-issued. v2 requires it on create, and the v1 modal never
  // collected it, so EVERY create answered `key: This field is required` and the modal
  // showed a generic "please try again" that named nothing. `projectKey` is the field the
  // modal now sends; `projectId` is what an edit round-trips (the server ignores a key
  // change on update, so sending it then is harmless).
  const key = (payload.projectKey ?? payload.projectId) as string | undefined;
  if (key !== undefined && key !== '') out.key = key;
  if (payload.startDate !== undefined) out.start_on = toDate(payload.startDate as number);
  if (payload.endDate !== undefined) out.end_on = toDate(payload.endDate as number);
  // Owner is a workspace member; v2 takes its primary key, not the display name the v1
  // form held. Optional and best-effort on purpose: a project without an owner is valid,
  // and a create must not fail because the person named has since left the roster.
  if (payload.owner !== undefined) {
    const ownerEmail = String(payload.owner).trim();
    if (!ownerEmail) {
      out.owner = null;
    } else {
      try {
        out.owner = await resolveId('member', ownerEmail);
      } catch {
        // An owner who cannot be resolved is dropped rather than fatal. The alternative —
        // failing the whole create over an optional field — is the silent-success bug
        // this codebase keeps hitting, inverted.
      }
    }
  }
  return out;
}

/**
 * A v1 time-log payload as a v2 time-entry write.
 *
 * Async because `project` and `work_item` are both `TenantRelatedField` and the v1 payload
 * carries a project key and an `ACME-14` key. Posting those strings answered
 * `Incorrect type. Expected pk value, received str.`, which is what logging time did after
 * the port.
 */
export async function timeLogWrite(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  const hours = Number(payload.hours ?? 0);
  const minutes = Number(payload.minutes ?? 0);
  if (payload.hours !== undefined || payload.minutes !== undefined) {
    // One integer. v1 stored the pair separately, which permitted `1h 90m`.
    out.minutes = Math.round(hours * 60 + minutes);
  }
  if (payload.projectId !== undefined && payload.projectId) {
    out.project = await resolveId('project', String(payload.projectId));
  }
  if (payload.taskId !== undefined && payload.taskId) {
    out.work_item = await resolveWorkItemId(String(payload.taskId));
  }
  if (payload.logDate !== undefined) out.spent_on = toDate(payload.logDate as number);
  if (payload.notes !== undefined) out.notes = payload.notes;
  if (payload.isBillable !== undefined) {
    out.billing_status = payload.isBillable ? 'billable' : 'non_billable_internal';
  }
  return out;
}
